import { del, get, patch, post } from "@/services/axiosUitils";

const basePath = "/pts_app/leave-approval";

export type LeaveType = "full-day" | "half-day" | "wfh" | string;

export type LeaveApprovalStatus = "pending" | "approved" | "reject" | string;

export interface LeaveApprovalItem {
  id: string;
  start_date: string;
  end_date: string;
  leave_type: LeaveType;
  status: LeaveApprovalStatus;
  requested_by: string;
  requested_by_name?: string;
  reason?: string;
  created_on?: string;
  updated_on?: string;
}

export interface CreateLeaveApprovalPayload {
  start_date: string;
  end_date: string;
  leave_type: LeaveType;
  requested_by: string;
  reason: string;
}

export interface LeaveApprovalCards {
  pending: number;
  approved: number;
  reject: number;
  wfh: number;
  full_day: number;
  half_day: number;
}

export interface LeaveApprovalListParams {
  page?: number;
  page_size?: number;
  start_date?: string;
  end_date?: string;
  leave_type?: string;
  status?: string;
  search?: string;
  requested_by?: string;
}

export interface LeaveApprovalResponse {
  success?: { code?: string; message?: string };
  links?: { next?: string | null; previous?: string | null };
  count: number;
  page?: number;
  page_size?: number;
  results: LeaveApprovalItem[];
  cards?: LeaveApprovalCards;
}

function unwrapList(response: unknown): LeaveApprovalItem[] {
  if (!response) {
    return [];
  }

  if (Array.isArray(response)) {
    return response as LeaveApprovalItem[];
  }

  const data = response as Record<string, unknown>;
  if (Array.isArray(data.data)) {
    return data.data as LeaveApprovalItem[];
  }
  if (Array.isArray(data.results)) {
    return data.results as LeaveApprovalItem[];
  }

  return [];
}

function emptyCards(): LeaveApprovalCards {
  return {
    pending: 0,
    approved: 0,
    reject: 0,
    wfh: 0,
    full_day: 0,
    half_day: 0,
  };
}

function numberOrZero(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function mapLeaveTypeToCardKey(
  leaveType?: string | null
): "wfh" | "full_day" | "half_day" | null {
  const value = String(leaveType ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");

  if (value === "wfh" || value === "work from home") {
    return "wfh";
  }
  if (value === "full day") {
    return "full_day";
  }
  if (value === "half day") {
    return "half_day";
  }
  return null;
}

class PtsLeaveApprovalService {
  /**
   * List leave approvals.
   * Calls: GET /pts_app/leave-approval/
   */
  getLeaveApprovals = async (
    params?: LeaveApprovalListParams
  ): Promise<LeaveApprovalResponse> => {
    const cleanedParams = Object.fromEntries(
      Object.entries(params ?? {}).filter(
        ([, value]) => value !== undefined && value !== null && value !== ""
      )
    );
    const response = await get<unknown>(`${basePath}/`, {
      params: cleanedParams,
    });
    const results = unwrapList(response);

    let count = results.length;
    let cards = emptyCards();

    if (response && typeof response === "object" && !Array.isArray(response)) {
      const data = response as Record<string, unknown>;
      if (typeof data.count === "number") {
        count = data.count;
      }
      if (data.cards && typeof data.cards === "object") {
        const apiCards = data.cards as Record<string, unknown>;
        cards = {
          pending: numberOrZero(apiCards.pending),
          approved: numberOrZero(apiCards.approved),
          reject: numberOrZero(apiCards.reject ?? apiCards.rejected),
          wfh: numberOrZero(apiCards.wfh),
          full_day: numberOrZero(
            apiCards.full_day ?? apiCards["full-day"] ?? apiCards.fullDay
          ),
          half_day: numberOrZero(
            apiCards.half_day ?? apiCards["half-day"] ?? apiCards.halfDay
          ),
        };
      } else {
        cards = results.reduce((acc, item) => {
          const status = String(item.status ?? "")
            .trim()
            .toLowerCase();
          if (status === "approved") {
            acc.approved += 1;
          } else if (status === "reject" || status === "rejected") {
            acc.reject += 1;
          } else if (status.includes("pending")) {
            acc.pending += 1;
          }

          const leaveTypeKey = mapLeaveTypeToCardKey(item.leave_type);
          if (leaveTypeKey) {
            acc[leaveTypeKey] += 1;
          }
          return acc;
        }, emptyCards());
      }
    }

    return {
      results,
      count,
      cards,
    };
  };

  /**
   * Create a leave approval.
   * Calls: POST /pts_app/leave-approval/
   */
  createLeaveApproval = async (
    payload: CreateLeaveApprovalPayload
  ): Promise<LeaveApprovalItem | undefined> => {
    return await post<LeaveApprovalItem>(`${basePath}/`, payload, {
      notifyMessage: true,
    });
  };

  /**
   * Update a leave approval form.
   * Calls: PATCH /pts_app/leave-approval/{id}/
   */
  updateLeaveApprovalForm = async (
    id: string,
    payload: CreateLeaveApprovalPayload
  ): Promise<LeaveApprovalItem | undefined> => {
    return await patch<LeaveApprovalItem>(`${basePath}/${id}/`, payload, {
      notifyMessage: true,
    });
  };

  /**
   * Update leave approval fields / status.
   * Calls: PATCH /pts_app/leave-approval/{id}/
   */
  updateLeaveApproval = async (
    id: string,
    payload: Record<string, unknown>
  ): Promise<LeaveApprovalItem | undefined> => {
    return await patch<LeaveApprovalItem>(`${basePath}/${id}/`, payload, {
      notifyMessage: true,
    });
  };

  /**
   * Bulk update leave approvals status.
   * Calls: PATCH /pts_app/leave-approval/bulk-status/
   */
  bulkUpdateStatus = async (
    ids: string[],
    status: LeaveApprovalStatus
  ): Promise<unknown> => {
    return await patch(
      `${basePath}/bulk-status/`,
      { ids, status },
      { notifyMessage: true }
    );
  };

  /**
   * Delete a leave approval.
   * Calls: DELETE /pts_app/leave-approval/{id}/
   */
  deleteLeaveApproval = async (id: string): Promise<unknown> => {
    return await del(`${basePath}/${id}/`, { notifyMessage: true });
  };
}

const instance = new PtsLeaveApprovalService();
export default instance;
